/**
 * What a structure import did, and what it left for a person to do.
 *
 * The counts are a receipt. The part that matters is the list of depots the
 * file did not attach to a company: they were imported — refusing them would
 * lose their place — and they are named here, highlighted, so the company is
 * chosen by someone who knows rather than guessed by the import.
 */

import { useTranslation } from "react-i18next";
import { Alert, Badge, Button } from "@hardauto/core-ui";

import type { RejectionReason, StructureImportReport } from "../../api";
import { NAMESPACE } from "../../i18n";

const KNOWN_REASONS: ReadonlySet<string> = new Set<RejectionReason>([
  "missing_depot_code",
  "missing_point_of_sale",
  "unknown_company",
  "value_too_long",
]);

export interface ImportReportProps {
  report: StructureImportReport;
  onDismiss: () => void;
  /** Show the depots tab narrowed to those without a company. */
  onFix: () => void;
}

export function ImportReport({ report, onDismiss, onFix }: ImportReportProps) {
  const { t } = useTranslation(NAMESPACE);
  const missing = report.depots_without_company;

  return (
    <div className="hardauto-structure__report">
      <Alert
        tone={missing.length || report.rejected.length ? "warning" : "success"}
        title={t("structure.import.title")}
        action={<Button onClick={onDismiss}>{t("structure.import.dismiss")}</Button>}
      >
        <p>
          {t("structure.import.summary", {
            pointsCreated: report.points_of_sale_created,
            pointsUpdated: report.points_of_sale_updated,
            depotsCreated: report.depots_created,
            depotsUpdated: report.depots_updated,
          })}
        </p>
        {missing.length ? (
          <>
            <p>{t("structure.import.withoutCompany", { count: missing.length })}</p>
            <ul
              className="hardauto-structure__missing"
              aria-label={t("structure.import.missingLabel")}
            >
              {missing.map((code) => (
                <li key={code}>
                  <Badge tone="warning">{code}</Badge>
                </li>
              ))}
            </ul>
            <Button variant="primary" size="sm" onClick={onFix}>
              {t("structure.import.fix")}
            </Button>
          </>
        ) : null}
        {report.rejected.length ? (
          <>
            <p>{t("structure.import.rejected", { count: report.rejected.length })}</p>
            <ul>
              {report.rejected.map((row) => (
                <li key={`${row.row}:${row.reason}`}>
                  {t("structure.import.rejectedRow", {
                    row: row.row,
                    reason: KNOWN_REASONS.has(row.reason)
                      ? t(`structure.import.reason.${row.reason}`, { value: row.value })
                      : row.reason,
                  })}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Alert>
    </div>
  );
}
